import { useEffect, useRef } from 'react';
import {
  registerSalesScreensPageApi,
  SalesScreensPageApi,
  SalesScreensPageType,
  SalesScreenInfo,
} from '../../aichat/agent/uiBridge';

// ============================================================================
// AI AJAN KÖPRÜSÜ — Satış Ekranları Yönetim Sayfaları (ortak hook)
// QuickSalePage (/hizli-satis), RestaurantPage (/restoran) ve
// OrderManagementPage (/siparis-yonetimi) aynı sözleşmeyle kayıt olur:
// ekran listeleme, ada göre ayar modalı açma, liste yenileme.
// Operasyonel POS ekranları kapsam dışıdır.
// Desen: useInvoiceAgentBridge ile aynı (cfgRef → stale closure koruması).
// ============================================================================

export interface SalesScreensPageAgentBridgeConfig {
  /** Sayfada yüklü ekranlar (ad + şube). */
  screens: Array<{ id: string; name: string; branchName?: string | null }>;
  /** Ekranın ayar modalını açar. */
  openSettings: (screen: { id: string; name: string }) => void;
  /** Ekran listesini API'den yeniden yükler. */
  refreshScreens: () => Promise<void>;
}

/** TR-toleranslı ad karşılaştırması için normalize eder. */
const normalizeName = (value: string): string =>
  value
    .toLocaleLowerCase('tr-TR')
    .replace(/[çÇ]/g, 'c')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[ıİiI]/g, 'i')
    .replace(/[öÖ]/g, 'o')
    .replace(/[şŞ]/g, 's')
    .replace(/[üÜ]/g, 'u')
    .trim();

const SCREEN_KIND_LABEL: Record<SalesScreensPageType, string> = {
  quickSale: 'hızlı satış ekranı',
  restaurant: 'restoran ekranı',
  package: 'paket sipariş ekranı',
};

export const useSalesScreensPageAgentBridge = (
  pageType: SalesScreensPageType,
  config: SalesScreensPageAgentBridgeConfig
): void => {
  // Kayıtlı API sabit kalır; en güncel state/handler'lara ref üzerinden erişilir.
  const cfgRef = useRef(config);
  cfgRef.current = config;

  useEffect(() => {
    const api: SalesScreensPageApi = {
      listScreens: (): SalesScreenInfo[] =>
        cfgRef.current.screens.map((s) => ({
          id: s.id,
          name: s.name,
          branchName: s.branchName ?? null,
        })),

      openSettings: (screenName: string) => {
        const screens = cfgRef.current.screens;
        const wanted = normalizeName(screenName);
        // Önce tam eşleşme, yoksa içeren tek kayıt
        let found = screens.find((s) => normalizeName(s.name) === wanted);
        if (!found) {
          const partial = screens.filter((s) => normalizeName(s.name).includes(wanted));
          if (partial.length === 1) found = partial[0];
          else if (partial.length > 1) {
            return {
              success: false,
              error: `"${screenName}" ile eşleşen birden fazla ekran var: ${partial
                .map((s) => s.name)
                .join(', ')} — tam adı belirt.`,
            };
          }
        }
        if (!found) {
          return {
            success: false,
            error: `"${screenName}" adında ${SCREEN_KIND_LABEL[pageType]} bulunamadı.`,
            availableScreens: screens.map((s) => s.name),
          };
        }
        cfgRef.current.openSettings({ id: found.id, name: found.name });
        return { success: true, screenId: found.id, screenName: found.name };
      },

      refreshScreens: async () => {
        await cfgRef.current.refreshScreens();
      },
    };

    return registerSalesScreensPageApi(pageType, api);
  }, [pageType]);
};

export default useSalesScreensPageAgentBridge;
